import { Component, ErrorInfo, ReactNode } from 'react';

import rollbar from '../rollbarClient';
import AppErrorFallback from './AppErrorFallback';

const RELOAD_STORAGE_KEY = 'chunk_error_last_reload';
const RELOAD_COOLDOWN_MS = 30_000;

// Exact messages emitted by each browser engine when a dynamic import() fails
// (e.g. 404 or wrong Content-Type after a new deploy). Vite always surfaces these
// as TypeError, so we validate the type first to avoid false positives from
// unrelated errors that happen to contain similar substrings.
const DYNAMIC_IMPORT_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome / Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
] as const;

function isChunkLoadError(error: Error): boolean {
  return error instanceof TypeError && DYNAMIC_IMPORT_MESSAGES.some((msg) => error.message.includes(msg));
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    rollbar.warning('ChunkLoadError: auto-recovery reload triggered', {
      originalError: error.message,
      componentStack: errorInfo.componentStack,
      tag: 'chunk-version-mismatch',
    });

    const lastReload = Number(sessionStorage.getItem(RELOAD_STORAGE_KEY) || '0');
    const now = Date.now();

    if (now - lastReload > RELOAD_COOLDOWN_MS) {
      sessionStorage.setItem(RELOAD_STORAGE_KEY, String(now));
      window.location.reload();
    }
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return <AppErrorFallback />;
    }
    return children;
  }
}

export default ChunkErrorBoundary;
